<script setup>
import FormAssemblyEmbed from '@/components/FormAssemblyEmbed.vue'
import FormAssemblyInline from '@/components/FormAssemblyInline.vue'

defineProps({
  heading:     { type: String, default: 'Stay up-to-date with our latest news' },
  bullets:     { type: Array,  default: () => [] },
  formId:      { type: String, required: true },
  formBaseUrl: { type: String, default: 'https://www.tfaforms.com' },
  // When true, render the form via inline DOM injection (FormAssemblyInline) instead of
  // the cross-origin iframe. Inline puts FormAssembly's real markup in our page so scoped
  // CSS can hide parts of it (used by the Academy banner to strip the form's branding).
  // The homepage leaves this false and keeps the isolated iframe.
  inline:      { type: Boolean, default: false },
})
</script>

<template>
  <section class="stay-informed">
    <div class="banner">
      <div class="content">
        <div class="intro">
          <h2 class="heading">{{ heading }}</h2>

          <ul v-if="bullets.length" class="benefits">
            <li v-for="bullet in bullets" :key="bullet" class="benefit">
              <span class="check" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M6 12.5 10 16.5 18 8"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span class="label">{{ bullet }}</span>
            </li>
          </ul>
        </div>

        <!-- Inline mode (the live default for Home and the Academy banner): FormAssembly's
             real markup is injected into the page, so the scoped CSS below reaches every
             field/label/button — the form sits on a light-pink card and matches the site's
             own inputs + CTA (shared fa-form-fields mixin, same treatment as Contact).
             reCAPTCHA + submit happen on-page. The trade-off is FA's global stylesheets (a
             bare `*{}` reset + generic `.label`/`.preField` rules in wforms-layout.css) can
             leak onto the banner — contained by the resets in the `.signup.inline` block.

             Iframe mode (`inline` false): the cross-origin FormAssemblyEmbed, kept only for
             the ComponentGallery demo. Isolation walls off FA's CSS but also walls off ours,
             which is why the live forms no longer use it. -->
        <div class="signup" :class="{ inline }">
          <FormAssemblyInline
            v-if="inline"
            :formId="formId"
            :baseUrl="formBaseUrl"
          />
          <FormAssemblyEmbed
            v-else
            :formId="formId"
            :baseUrl="formBaseUrl"
            title="Newsletter sign-up form"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.stay-informed {
  // Shared page container — lands the purple banner at the same left/right
  // edges as the sibling .partner-band banner (centred 1200px column + gutter).
  max-width: $container;
  margin-inline: auto;
  padding-inline: $gutter;

  

  .banner {
    background: var(--color-banner);
    border-radius: $radius-lg;
    // A real `border` on a rounded corner can still show a hairline sliver of whatever's
    // behind the box even with overflow:hidden + background-clip — the border's own curve
    // and the clip boundary aren't guaranteed to rasterise in perfect agreement at every
    // zoom/DPR. An inset box-shadow paints the same 1px line but follows the box's actual
    // clipped shape exactly, since it's composited as part of the box, not a separate stroke.
    box-shadow: inset 0 0 0 1px var(--color-border-decorative);
    padding: 0px;
    overflow: hidden;
    //@include from-md { padding: $space-6 $space-12; }

    // Two columns from $bp-md: marketing intro left, form right; stacked below.
    // Side-by-side keeps the banner short (form beside the intro, not beneath it).
    .content {
      display: flex;
      flex-direction: column;
      gap: $space-6;
      @include from-md {
        flex-direction: row;
        align-items: flex-start;
        gap: $space-8;
      }
    }

    .intro {
      display: flex;
      flex-direction: column;
      gap: $space-6;
      padding: $space-6;
      @include from-md { flex: 1;   }
    }

    .heading {
      margin: 0;
      color: var(--color-on-banner);
      font-weight: 500;
      
      
      line-height: $lh-heading;
      letter-spacing: $ls-heading;
      @include from-md {
        font-size: $font-size-h2; font-weight: 700; line-height: 1.1;
      }
      @include from-lg {
        font-size: $font-size-h1; 
      }
    }

    .benefits {
      display: flex;
      flex-direction: column;
      gap: $space-2;
      margin: 0;
      padding: 0;
      list-style: none;

      .benefit {
        display: flex;
        align-items: center;
        gap: $space-2;

        .check {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: var(--color-banner-accent);
          color: var(--color-banner);
          svg { width: 15px; height: 15px; }
        }

        .label {
          color: var(--color-on-banner);
          font-size: $font-size-base;
          line-height: $lh-base;
        }
      }
    }

    // Wrapper for the embedded FormAssembly form. Two rendering paths (see template):
    .signup {
      width: 100%;
      
      @include from-md {
        flex: 1; min-width: 50%;
      }

      // Iframe path (ComponentGallery demo only): we can only shape the iframe's own box —
      // clipping to the site corner radius rounds the form's card against the purple banner.
      :deep(.fa-embed) {
        border-radius: $radius;
        overflow: hidden;
      }

      // Inline path (Home + Academy): the form's real markup is in our DOM. Give it a
      // readable light-pink card on the purple banner and style every field page-side via
      // the shared fa-form-fields mixin (same fields/inputs/validation/pill-button as Contact).
      &.inline {
        .fa-inline {
          background: var(--color-banner-card);
          border-radius: $radius-lg;
          // FormAssembly's own global stylesheet (wforms-layout.css) still applies some
          // default background/border deeper in its injected markup that the fa-form-fields
          // mixin doesn't fully neutralise — without a clip, it can poke out past this card's
          // rounded corners as a sliver of dark pixels against the purple banner behind it.
          overflow: hidden;
          padding: $space-4;
          @include from-md { padding: $space-6 $space-4; }


          // Collapse FA's generous outer spacing (48px block margin on .wFormContainer,
          // ~1.8em padding on .wForm) so it doesn't stack on top of the card padding.
          // (Form-level padding reset, flex-column, privacy reorder, `.section.inline` rows,
          // and <br> hiding all come from the shared fa-form-fields mixin, included below.)
          :deep(.wFormContainer) {
              margin-block: 0;
          
              .field-container-D {
                //background: red !important;
                margin-top:0px !important;
                margin-bottom:0px !important;
              }
            }
          :deep(.wForm) { padding-block: 0; }
          :deep(.wFormHeader) { margin: 0; }
          :deep(input[type="submit"]) {
            margin-top: 0px !important;
            border-radius: $radius-pill;
          }
          :deep(div.actions[data-contentid="submit_button"]) { margin-top: 0px !important; }
        }

        // All field/label/input/validation/submit-button styling — one shared source.
        // (FA's global wforms-layout.css can't wash out this banner's own heading/bullets:
        // StayInformed's `.heading` / `.benefits .label` rules out-specify FA's bare `*{}`.)
        @include fa-form-fields;
      }
    }
  }
}
</style>
